import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const ForgotPassword = () => {
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();
  const mobileRef = useRef(null);
  const otpRef = useRef(null);
  const passwordRef = useRef(null);

  useEffect(() => {
    document.title = "Enova E - Ticketing";
  }, []);

  useEffect(() => {
    if (step === 1 && mobileRef.current) mobileRef.current.focus();
    if (step === 2 && otpRef.current) otpRef.current.focus();
    if (step === 3 && passwordRef.current) passwordRef.current.focus();
  }, [step]);

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);
    try {
      const res = await api.post('/auth/send-otp', { mobile });
      if (res.data.status) {
        setIsSuccess(true);
        setMessage('OTP sent to your mobile.');
        setStep(2);
      } else {
        setIsSuccess(false);
        setMessage(res.data.message || 'Mobile not found.');
      }
    } catch {
      setIsSuccess(false);
      setMessage('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setMessage('');
    setSubmitting(true);
    try {
      const res = await api.post('/auth/verify-otp', { mobile, otp });
      if (res.data.status) {
        setIsSuccess(true);
        setMessage('OTP verified. Please enter your new password.');
        setStep(3);
      } else {
        setIsSuccess(false);
        setMessage(res.data.message || 'Invalid OTP.');
      }
    } catch {
      setIsSuccess(false);
      setMessage('An error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
  
    const password = newPassword?.trim();
  
    // Password empty check
    if (!password) {
      setIsSuccess(false);
      setMessage("Password is required.");
      return;
    }
  
    // Password format validation (with # added)
    const passwordRegex = /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@#$!%*?&])[A-Za-z\d@#$!%*?&]{6,}$/;
  
    if (!passwordRegex.test(password)) {
      setIsSuccess(false);
      setMessage(
        "Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character (min 6 characters)."
      );
      return;
    }
  
    // Confirm password check
    if (password !== confirmPassword.trim()) {
      setIsSuccess(false);
      setMessage("Passwords do not match.");
      return;
    }
  
    setMessage("");
    setSubmitting(true);
  
    try {
      const res = await api.post('/auth/reset-password', { 
        mobile, 
        otp, 
        new_password: password 
      });
  
      if (res.data.status) {
        setIsSuccess(true);
        setMessage("Password reset successful. Redirecting...");
        setTimeout(() => navigate('/'), 3000);
      } else {
        setIsSuccess(false);
        setMessage(res.data.message || "Failed to reset password.");
      }
    } catch {
      setIsSuccess(false);
      setMessage("An error occurred. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (

      <div className="min-h-screen flex flex-col md:flex-row">
    {/* Left Side (Illustration) */}
    <div className="w-full md:w-1/2 bg-white flex justify-center items-center p-4 relative">
      <div className="text-center">
        <img src="/login_page.svg" alt="Helpdesk Illustration" className="w-full max-w-md mx-auto" />
        {/* <div className="mt-6">
          <img src="/logo.png" alt="ENOVA Logo" className="h-10 mx-auto" />
          <h1 className="text-lg font-bold text-[#B91C1C] mt-2">HELP TICKETING SYSTEM</h1>
        </div> */}
      </div>
      <span className="absolute top-2 left-4 text-xs text-[#B91C1C] font-semibold hidden">Welcome to ENOVA</span>
      <p className="text-center text-xs mt-6 text-gray-500 absolute bottom-2 left-4 text-xs">
        © {new Date().getFullYear()} ENOVA Software & Hardware Solutions Pvt Ltd
      </p>
    </div>

      <div className="w-full md:w-1/2 flex items-center justify-center p-6">
  <form
    onSubmit={step === 1 ? handleSendOtp : step === 2 ? handleVerifyOtp : handleResetPassword}
    className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-200"
  >
    <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">Forgot Password</h2>

    {/* Mobile Number */}
    <div className="mb-4">
      <label className="block mb-1 text-sm font-medium text-gray-700">Mobile Number</label>
      <input
        type="text"
        value={mobile}
        onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 10))}
        placeholder="Enter mobile number"
        ref={mobileRef}
        readOnly={step > 1}
        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
        required
      />
    </div>

    {/* OTP */}
    {step >= 2 && (
      <div className="mb-4">
        <label className="block mb-1 text-sm font-medium text-gray-700">OTP</label>
        <input
          type="text"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="Enter OTP"
          ref={otpRef}
          readOnly={step > 2}
          className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
          required
        />
      </div>
    )}

    {/* Password Reset */}
    {step === 3 && (
      <>
        <div className="mb-4">
          <label className="block mb-1 text-sm font-medium text-gray-700">New Password</label>
          <input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            ref={passwordRef}
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block mb-1 text-sm font-medium text-gray-700">Confirm Password</label>
          <input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
            required
          />
        </div>
      </>
    )}

    {/* Message */}
    {message && (
      <div className="mb-4 text-center text-sm font-semibold" style={{ color: isSuccess ? 'green' : 'red' }}>
        {message}
      </div>
    )}

    {/* Submit Button */}
    <button
      type="submit"
      disabled={submitting}
      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-2 rounded-lg flex justify-center items-center gap-2 transition-colors"
    >
      {submitting && (
        <svg
          className="animate-spin h-4 w-4 text-white"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-50" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-100" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
      )}
      <span>
        {submitting
          ? step === 1
            ? 'Sending OTP...'
            : step === 2
            ? 'Verifying...'
            : 'Resetting...'
          : step === 1
          ? 'Send OTP'
          : step === 2
          ? 'Verify OTP'
          : 'Reset Password'}
      </span>
    </button>

    <p className="text-center text-xs text-gray-500 mt-6">
       ⚠ Enova Support will never request your password. Sharing it may compromise your data security.
    </p>
    <p className="text-center text-xs text-gray-500 mt-6">
      © {new Date().getFullYear()} ENOVA Software & Hardware Solutions Pvt Ltd
    </p>
  </form>
</div>

    </div>
  );
};

export default ForgotPassword;
